/**
 * THE FIVE SITUATIONS THE APPLICANT CAN BE IN, and what each of them asks them to do next.
 *
 * `ApplicationState` on the wire has six values. This module narrows them to FIVE and that
 * narrowing is deliberate:
 *
 *   `rejected` IS UNREACHABLE AND MUST NOT BE BUILT. The schema, the admin contract and the
 *   oracles all rule it out — `work_queue_decision` seeds exactly two outcomes for kind
 *   `application` (approved, needs_more_info); `decideApplication` on admin-api says "Two
 *   outcomes, and only two … There is no `rejected` and no terminal refused state"; and
 *   `author_transition_rule` has no `applicant -> disabled` row. The value survives on
 *   `authors-api.yaml` only as a stale enum member that the corpus has already flagged.
 *   A screen that renders a refusal no author can ever receive is worse than a dead branch,
 *   because it tells the next reader of this code that refusal is possible. So it is mapped here,
 *   once, onto `needs_more_info` — the only refusal that exists — and nothing downstream sees it.
 *
 * THE OTHER FIVE, and where each comes from (getApplication's own description of its three-table
 * join): `empty` and `ready` are computed from the completeness checklist BEFORE anything is
 * submitted; `in_review` is an open `work_queue_item` of kind `application`; `approved` and
 * `needs_more_info` are that item's decision.
 */
import type { TFunction } from 'i18next';
import { i18n } from '../../../i18n';
import type { Application, ApplicationDecision, ApplicationState } from '../contract';

/** The five situations this feature actually renders. */
export type ApplicationSituation = 'empty' | 'ready' | 'in_review' | 'approved' | 'needs_more_info';

/**
 * Narrows the wire enum. `rejected` folds into `needs_more_info`, because that is the only
 * refusal the platform can produce and a refused applicant may re-apply for ever.
 */
export function narrowState(state: ApplicationState): ApplicationSituation {
  return state === 'rejected' ? 'needs_more_info' : state;
}

/** The same narrowing for the decision's own outcome, which carries the same stale value. */
export function narrowOutcome(
  outcome: ApplicationDecision['outcome'],
): 'approved' | 'needs_more_info' {
  return outcome === 'rejected' ? 'needs_more_info' : outcome;
}

/** What the author is being asked to do, right now, in one shape the screens can render. */
export interface NextStep {
  readonly situation: ApplicationSituation;
  /** The heading. Says what is true, not what we would like to be true. */
  readonly title: string;
  /** One or two plain sentences. Never a machine code and never a promise we cannot keep. */
  readonly body: string;
  /**
   * The single thing to do next, or `null` when the honest answer is "nothing — wait".
   * One action, never two: an application screen with two primary buttons is a screen that does
   * not know what it wants from the person reading it.
   */
  readonly action: NextAction | null;
  /** Whether the form is worth showing at all in this situation. */
  readonly showsForm: boolean;
  /** The tone the status header takes. Maps to a Mantine colour in the component, not here. */
  readonly tone: 'neutral' | 'ready' | 'waiting' | 'attention' | 'good';
}

/** The step's one action. Accepting the agreements is not one: Submit opens them (at the foot). */
export type NextAction =
  | { readonly kind: 'fill_in'; readonly label: string }
  | { readonly kind: 'submit'; readonly label: string }
  | { readonly kind: 'resubmit'; readonly label: string }
  | { readonly kind: 'open_cabinet'; readonly label: string };

export interface NextStepInput {
  readonly application: Application;
  /** From `getCurrentTerms`. Undefined while it is still loading. */
  readonly termsAccepted: boolean | undefined;
  /** How many required checklist rows are still unsatisfied. */
  readonly requiredOutstanding: number;
  /** `OwnProfile.slugFrozen`. Undefined, `approvedStep` (at the foot) says nothing of the address. */
  readonly slugFrozen?: boolean | undefined;

  /** Drawn beside the cabinet's sidebar. Undefined, as outside it. Only `approvedStep` reads it. */
  readonly insideCabinet?: boolean | undefined;
}

/**
 * The whole of "what must the author do next", in one function, so that the header, the checklist
 * and the action button can never disagree with each other.
 *
 * NOTE ON `in_review`: there is NOTHING to count down to. `work_queue_kind` seeds `application`
 * with `sla_working_days = NULL` and `reserve_hours = NULL`, and no operation on authors-api
 * returns a queue position, a deadline or an estimate. So this text promises no turnaround, and
 * inventing one here would be inventing a commitment the platform has not made.
 */
export function nextStep(
  input: NextStepInput,
  // A screen passes its own `t`; anything else reads the catalogue in the language now in force.
  t: TFunction<'application'> = i18n.getFixedT(null, 'application'),
): NextStep {
  const situation = narrowState(input.application.state);
  const { requiredOutstanding, termsAccepted } = input;

  switch (situation) {
    // An approved author's page is their profile, not an application: the step is built at the
    // foot of this file (`approvedStep`), where what it says about the address is argued.
    case 'approved':
      return approvedStep(input.slugFrozen, input.insideCabinet === true, t);

    case 'in_review':
      return {
        situation,
        title: t('status.inReview.title'),
        body: t('status.inReview.body'),
        action: null,
        showsForm: true,
        tone: 'waiting',
      };

    case 'needs_more_info':
      return {
        situation,
        title: t('status.needsMoreInfo.title'),
        body: t('status.needsMoreInfo.body'),
        action: { kind: 'resubmit', label: t('status.needsMoreInfo.action') },
        showsForm: true,
        tone: 'attention',
      };

    case 'ready':
      return {
        situation,
        title: t('status.ready.title'),
        body: t('status.ready.body'),
        action: { kind: 'submit', label: t('status.ready.action') },
        showsForm: true,
        tone: 'ready',
      };

    case 'empty':
    default: {
      if (termsAccepted === false && requiredOutstanding === 0) {
        return {
          situation: 'ready',
          title: t('status.ready.title'),
          body: t('status.ready.agreementsOwedBody'),
          action: { kind: 'submit', label: t('status.ready.action') },
          showsForm: true,
          tone: 'ready',
        };
      }
      const isBlank = input.application.completenessPercent === 0;
      return {
        situation: 'empty',
        title: isBlank ? t('status.empty.blankTitle') : t('status.empty.nearlyTitle'),
        body: isBlank
          ? t('status.empty.blankBody')
          : t('status.empty.nearlyBody', { count: requiredOutstanding }),
        action: {
          kind: 'fill_in',
          label:
            requiredOutstanding === 0 ? t('status.empty.carryOn') : t('status.empty.fillInMissing'),
        },
        showsForm: true,
        tone: 'neutral',
      };
    }
  }
}

/**
 * Whether the public page address may still be changed. It is editable until approval and frozen
 * after, enforced server-side by the `author_slug_freeze` trigger and surfaced as 409 SLUG_FROZEN.
 * `OwnProfile.slugFrozen` is the authority; the situation is only the reason to explain.
 */
export function slugIsEditable(slugFrozen: boolean | undefined): boolean {
  return slugFrozen !== true;
}

/* ============================================ the approved author's page, and the address rule */

/**
 * ★ WHAT MAY STILL HAPPEN TO THE PUBLIC PAGE ADDRESS - one answer, read by the status header and by
 * the address field, so the two can never disagree (design 14, finding F3).
 *
 * THE CABINET WALK OF 2026-10-01 FOUND THEM DISAGREEING. An author the import brought over from the
 * old apps opened their profile and was told "Your public page address is now fixed and cannot be
 * changed", while the field under it was EMPTY and editable. Both could not be true, and the field
 * was the one that was right: the freeze is `status = 'approved' AND an address exists`
 * (src/modules/authors/services/profile.presenter.ts, `slugFrozen`; the database trigger
 * `author_slug_freeze` refuses on the same condition), so an approved author with no address yet
 * may still choose one - once - and an imported author is exactly that author.
 *
 *   fixed            `slugFrozen` - the address is chosen and approved; nothing changes it.
 *   choose_once      approved with no address yet: the first one they save is the last.
 *   until_approval   an applicant: change it freely now, and it freezes on approval.
 *
 * `slugFrozen` is the server's own answer (`OwnProfile.slugFrozen`), never inferred here from the
 * situation alone - an approval does not freeze an address that does not exist.
 */
export type AddressRule = 'fixed' | 'choose_once' | 'until_approval';

export function addressRule(situation: ApplicationSituation, slugFrozen: boolean): AddressRule {
  if (slugFrozen) {
    return 'fixed';
  }
  return situation === 'approved' ? 'choose_once' : 'until_approval';
}

/** The one sentence each rule gets in the approved author's header, as catalogue keys. */
const ADDRESS_SENTENCE = {
  fixed: 'status.approved.address.fixed',
  choose_once: 'status.approved.address.chooseOnce',
} as const satisfies Readonly<Record<Exclude<AddressRule, 'until_approval'>, string>>;

/**
 * ★ THE APPROVED AUTHOR'S STEP - THEIR PROFILE, NOT AN APPLICATION (design 14, F3). Nothing on this
 * page is sent anywhere any more, so the header says where they are and what the address allows,
 * and offers the cabinet. "Your application was approved" is NOT said here: an author the import
 * brought over never applied on this platform, and an author who did is told so by the decision
 * notice above the form (`components/DecisionNotice.tsx`), which renders only when a decision
 * exists. `slugFrozen` undefined - the caller does not know - says nothing about the address.
 *
 * ★ INSIDE THE CABINET IT IS JUST THE PROFILE, WITH NO BUTTON (the owner's live walk of real
 * cabinets, 2026-10-01). Since F13 the page sits beside the cabinet's sidebar for every author the
 * cabinet is open to, and there "Go to your cabinet" led to where the reader already was, under a
 * welcome ("you can start writing") said to authors with a hundred articles. So inside, the header
 * names the page and says what it is for, and there is no action: a profile has nothing to send.
 * The welcome and the button stay for the one reader still outside - the page drawn in its own
 * frame (`ApplyFrame`, src/router.tsx), which is an applicant just approved whose session has not
 * caught up yet, or the default build's fixture - where the button is the only way in.
 */
function approvedStep(
  slugFrozen: boolean | undefined,
  insideCabinet: boolean,
  t: TFunction<'application'>,
): NextStep {
  const address =
    slugFrozen === undefined ? '' : ` ${t(ADDRESS_SENTENCE[slugFrozen ? 'fixed' : 'choose_once'])}`;
  if (insideCabinet) {
    return {
      situation: 'approved',
      title: t('status.approved.insideTitle'),
      body: `${t('status.approved.insideBody')}${address}`,
      action: null,
      showsForm: true,
      tone: 'good',
    };
  }
  return {
    situation: 'approved',
    title: t('status.approved.outsideTitle'),
    body: `${t('status.approved.outsideBody')}${address}`,
    action: { kind: 'open_cabinet', label: t('status.approved.action') },
    showsForm: true,
    tone: 'good',
  };
}

/*
 * ★ THE AUTHOR AGREEMENT IS THE LAST STEP, NOT THE FIRST (design 25; design 24, T24). `termsAccepted`
 * is false while a submission document is owed (../domain/agreements.ts). An empty application used
 * to be asked for the agreement before anything else; now it is asked to fill in, and a complete one
 * that still owes a document is "Ready to submit" - its "Submit for review" opens "Our standards and
 * agreement" (../screens/AgreementStep.tsx), which accepts and submits in one press. No situation
 * draws an action of accepting alone, so `NextAction` has none (design 24, batch 2).
 */
